//Welcome screen as the user successfully added trial services to the test app.
//  Last Step of onboarding.

// Library Utilities
import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

// Styles
import Styles from "./appCreationSuccess.module.scss";
import {
  primaryButtonStyle,
  primaryButtonHoverStyle,
} from "../../../Shared/buttonStyles";

// Components
import Button from "../../../Shared/button/button";
import AuthSidebar from "../../auth/authSidebar/authSidebar";
import InformativeText from "../../auth/informativeText/informativeText";
import ClipEducation from "../../../assets/images/Generating_API_key_dashboard_illustration.svg";
import TransitionPopUp from "../transition-pop-up/transitionPopUp";
import Toast from "../../../Shared/toast/toast";
import AddAppKey from "../../application/manage-apps/add-api-key/addApiKey";
import { useTitle } from "../../../Shared/useTitle";

// Constants
import { LARGE } from "../../../Shared/buttonSize";

// Functions
import { callApi } from "../../../api/fetch";
import { productPID, isAutoApprovedProduct, productName } from "../../../utils/dashboardMappers";
import { testIdProps } from "../../../utils/testIdProps";

function AppCreationSuccess() {
  const {
    state: { subscribedService: selectedServices },
  }: any = useLocation();
  const history = useNavigate();
  const test_app_id = localStorage.getItem("test_app_id") || "";
  const [toggleApiKeyPopUp, setToggleApiKeyPopUp] = useState(false);
  //user will not be able to use browser back once the onboarding state is passed
  window.addEventListener("popstate", () => {
    window.history.go(1);
  });
  const subscribedService = selectedServices.filter(
    (selectedService: any) => isAutoApprovedProduct(selectedService)
  );
  const approvalNeeded = selectedServices.filter(
    (selectedService: any) => selectedService.is_approval_required === true
  );
  const [generateApiKeyLoading, setGenerateApiKeyLoading] =
    useState(false);
  const [toggleSkipForNowTransitionPopUp, setToggleSkipForNowTransitionPopUp] =
    useState(false);
  const messageArray = [
    "You can use the Trial Application created for you to test our services instantly.",
    "Go to Products to select the required services.",
    "Refer the documentations to guide you through the process and help you get started immediately.",
  ];
  const [loaderMessage, setLoaderMessage] = useState(messageArray[0]);
  const [apiKey, setApiKey] = useState("");

  function toastTimeout(
    showToastValue: boolean,
    toastType: "success" | "error",
    toastMessage: string | string[],
    heading?: string
  ) {
    if (!showToastValue) return;

    // Show toast via react-toastify
    Toast(
      toastType,
      toastMessage,
      heading || (toastType === "success" ? "Success" : "Error")
    );
  }
  //set timer for the welcome Modal to appear while displaying welcome message
  function setIntervalFunction() {
    let i = 1;
    const timer = setInterval(() => {
      if (i >= messageArray.length) {
        clearInterval(timer);
        history("/application/dashboard");
        return;
      }
      setLoaderMessage(messageArray[i]);
      i += 1;
    }, 3000);
    return;
  }
  function goToManageAppsHandler() {
    setGenerateApiKeyLoading(true);
    callApi(`/apps/${test_app_id}/create-key`, {
      method: "POST",
      data: { key_name: "Test Key" },
    })
      .then((key) => {
        setApiKey((key as Record<string, unknown>).api_key as string);
        setGenerateApiKeyLoading(false);
        setToggleApiKeyPopUp(true);
      })
      .catch((error) => {
        setGenerateApiKeyLoading(false);
        setToggleSkipForNowTransitionPopUp(false);
        toastTimeout(true, "error", error.message);
        // TODO: add sentry error
      });
  }
  function handleGoToChecklist() {
    setToggleSkipForNowTransitionPopUp(true);
    setIntervalFunction();
    return;
  }
  useTitle("Zoop Customer Platform | Generate API Key");
  return (
    <div>
      <div
        className="container-fluid p-0"
        style={
          toggleSkipForNowTransitionPopUp
            ? { filter: "blur(5px)" }
            : { filter: "blur(0)" }
        }
      >
        {toggleApiKeyPopUp && (
          <AddAppKey
            onCancel={() => {
              setToggleApiKeyPopUp(false);
              history(`/application/manage-apps/${test_app_id}`);
            }}
            apiKey={apiKey}
          />
        )}

        <div className="row no-gutters">
          <div className="col-xl-5 d-none d-xl-block">
            <AuthSidebar
              img={
                <img src={ClipEducation} alt="ClipEducation" width="350px" />
              }
              info={
                <InformativeText
                  headertext="Generate your API key"
                  bodytext="You can also generate the API key later from the Manage Apps
                  section."
                />
              }
            />
          </div>
          <div className="col-xl-7 col-lg-12">
            <div className={`${Styles.authActionsBackground}`}>
              <div
                style={{ height: "15%" }}
                className="d-flex align-items-center justify-content-center"
              >
                <div className="py-3">
                  <p className={`${Styles.headingText} text-center mb-0`}>
                    We have created an application for you
                  </p>
                </div>
              </div>
              <div style={{ height: "70%" }}>
                <div
                  className="container px-0 py-3"
                  style={{ minHeight: "450px", overflow: "auto" }}
                >
                  {approvalNeeded.length !== 0 &&
                  subscribedService.length !== 0 ? (
                    <div className="row no-gutters">
                      <div
                        className="col-6"
                        style={{
                          borderRight: "0.2px solid #dfdfdf",
                          height: "450px",
                        }}
                      >
                        <p
                          className={`mb-0 text-center ${Styles.subscribeLabelText}`}
                        >
                          Subscribed Products
                        </p>
                        <div className="py-3">
                          {subscribedService.map((service: any) => {
                            return (
                              <div
                                className={`${Styles.serviceLevelSettings} py-2 px-3 d-flex align-items-center`}
                                key={productPID(service)}
                                style={{ width: "80%" }}
                              >
                                <div className="px-1">
                                  <p className={`mb-0 ${Styles.serviceName}`}>
                                    {productName(service)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="col-6">
                        <p
                          className={`mb-0 text-center ${Styles.waitingForApprovalLabelText}`}
                        >
                          Waiting for approval
                        </p>
                        <div className="py-3">
                          {approvalNeeded.map((service: any) => {
                            return (
                              <div
                                className={`${Styles.serviceLevelSettings} py-2 px-3 d-flex align-items-center`}
                                key={productPID(service)}
                                style={{ width: "80%" }}
                              >
                                <div className="px-1">
                                  <p className={`mb-0 ${Styles.serviceName}`}>
                                    {productName(service)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : subscribedService.length !== 0 ? (
                    <div className="row no-gutters">
                      <div className="col-12">
                        <p
                          className={`mb-0 text-center ${Styles.subscribeLabelText}`}
                        >
                          Subscribed Products
                        </p>
                        <div className="py-3">
                          {subscribedService.map((service: any) => {
                            return (
                              <div
                                className={`${Styles.serviceLevelSettings} py-2 px-3 d-flex align-items-center`}
                                key={productPID(service)}
                                style={{ width: "50%" }}
                              >
                                <div className="px-1">
                                  <p className={`mb-0 ${Styles.serviceName}`}>
                                    {productName(service)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : approvalNeeded.length !== 0 ? (
                    <div className="row no-gutters">
                      <div className="col-12">
                        <p
                          className={`mb-0 text-center ${Styles.waitingForApprovalLabelText}`}
                        >
                          Waiting for approval
                        </p>
                        <div className="py-3">
                          {approvalNeeded.map((service: any) => {
                            return (
                              <div
                                className={`${Styles.serviceLevelSettings} py-2 px-3 d-flex align-items-center`}
                                key={productPID(service)}
                                style={{ width: "50%" }}
                              >
                                <div className="px-1">
                                  <p className={`mb-0 ${Styles.serviceName}`}>
                                    {productName(service)}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div
                style={{ height: "15%" }}
                className="d-flex align-items-center justify-content-center"
              >
                <div className="py-3">
                  {subscribedService.length === 0 ? (
                    <div style={{ margin: "0 auto" }}>
                      <Button
                        id="go-to-dashboard"
                        size={LARGE}
                        hoveredStyle={primaryButtonHoverStyle}
                        onClick={handleGoToChecklist}
                        style={primaryButtonStyle}
                      >
                        Go to Dashboard
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ margin: "0 auto" }}>
                        <Button
                          id="generate-key"
                          size={LARGE}
                          isLoading={generateApiKeyLoading}
                          hoveredStyle={primaryButtonHoverStyle}
                          onClick={goToManageAppsHandler}
                          style={primaryButtonStyle}
                        >
                          Generate Key
                        </Button>
                      </div>
                      <div className="py-2">
                        <div
                          {...testIdProps("skip-for-now")}
                          className={`${Styles.blockButton}`}
                          onClick={handleGoToChecklist}
                        >
                          <p className={`${Styles.linkText} mb-0`}>
                            Skip for now
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {toggleSkipForNowTransitionPopUp ? (
        <TransitionPopUp
          onCancel={() => setToggleSkipForNowTransitionPopUp(false)}
          headingText="Welcome! Your account setup is complete!"
        >
          <p className={`mb-0 ${Styles.stepText}`}>{loaderMessage}</p>
        </TransitionPopUp>
      ) : null}
    </div>
  );
}

export default AppCreationSuccess;
